import { useState } from "react";
import { toast } from "sonner";
import { Building2, Clock, Lock, XCircle } from "lucide-react";
import UpiPayCard from "../UpiPayCard";
import { BRAND, errMsg } from "./constants";

const TYPES = ["dormitory", "pg", "hostel", "lodge", "homestay", "hotel", "farmstay", "holiday"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>{label}</span>
    {children}
  </label>
);

const inputCls = "mt-1 w-full rounded-xl border-2 px-3 py-2.5 text-sm outline-none focus:border-[#00604A] bg-white";

export function RegisterPropertyForm({ authFetch, API, onCreated }) {
  const [f, setF] = useState({ name: "", property_type: "dormitory", city: "", locality: "", address: "", phone: "", total_beds_estimate: 10 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authFetch({ method: "post", url: `${API}/homestay/properties`, data: { ...f, total_beds_estimate: Number(f.total_beds_estimate) || 10, address: f.address || null } });
      toast.success("Property submitted — admin will verify shortly");
      onCreated();
    } catch (err) {
      toast.error(errMsg(err, "Could not submit property"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="hs-register-form" className="rounded-3xl border-2 bg-white p-5 md:p-7 shadow-sm" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest" style={{ color: BRAND.orange }}>
        <Building2 className="w-4 h-4" /> Register your property
      </div>
      <h2 className="mt-2 font-display text-2xl md:text-3xl font-bold" style={{ color: BRAND.green }}>List once. Run it from your phone.</h2>
      <p className="text-sm mt-1" style={{ color: BRAND.muted }}>Our team verifies every property. Your 7-day Featured trial starts the moment you are approved.</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Field label="Property name"><input required data-testid="hs-name-input" className={inputCls} value={f.name} onChange={set("name")} placeholder="Sunrise Dormitory" /></Field>
        <Field label="Type">
          <select data-testid="hs-type-select" className={inputCls} value={f.property_type} onChange={set("property_type")}>
            {TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
        </Field>
        <Field label="City"><input required data-testid="hs-city-input" className={inputCls} value={f.city} onChange={set("city")} placeholder="Pune" /></Field>
        <Field label="Locality"><input required data-testid="hs-locality-input" className={inputCls} value={f.locality} onChange={set("locality")} placeholder="Kothrud" /></Field>
        <Field label="Contact phone"><input required data-testid="hs-phone-input" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="98xxxxxxxx" /></Field>
        <Field label="Approx. beds"><input type="number" min={1} data-testid="hs-beds-input" className={inputCls} value={f.total_beds_estimate} onChange={set("total_beds_estimate")} /></Field>
        <div className="sm:col-span-2"><Field label="Address (optional)"><input data-testid="hs-address-input" className={inputCls} value={f.address} onChange={set("address")} /></Field></div>
      </div>
      <button disabled={busy} data-testid="hs-register-submit" className="mt-5 rounded-full px-6 py-3 font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ background: BRAND.orange }}>
        {busy ? "Submitting…" : "Submit for verification →"}
      </button>
    </form>
  );
}

export function StatusCard({ property }) {
  const rejected = property.status === "rejected";
  const Icon = rejected ? XCircle : Clock;
  return (
    <div data-testid="hs-status-card" className="rounded-3xl border-2 p-6 bg-white" style={{ borderColor: rejected ? "#DC2626" : BRAND.orange }}>
      <Icon className="w-8 h-8" style={{ color: rejected ? "#DC2626" : BRAND.orange }} />
      <h2 className="mt-3 font-display text-2xl font-bold" style={{ color: BRAND.green }}>{property.name}</h2>
      <p className="mt-1 text-sm" style={{ color: BRAND.muted }} data-testid="hs-status-text">
        {rejected ? "This property was not approved. Reach us on WhatsApp for details." : "Under verification by HeylpMate admin. You'll get your 7-day Featured trial as soon as it's approved."}
      </p>
    </div>
  );
}

export function LockedCard({ property, price = 999 }) {
  return (
    <div data-testid="hs-locked-card" className="grid md:grid-cols-2 gap-5">
      <div className="rounded-3xl p-6 text-white" style={{ background: BRAND.green }}>
        <Lock className="w-8 h-8" />
        <h2 className="mt-3 font-display text-3xl font-bold">Your trial has ended.</h2>
        <p className="mt-2 text-sm text-white/85">Unlock the Owner Command Centre and Live Bed Board for {property.name} — ₹{price}/month, less than ₹34 a day.</p>
        <ul className="mt-4 space-y-1 text-sm text-white/90 list-disc pl-5">
          <li>Live 2D + isometric 3D bed board</li>
          <li>Revenue vs expenses, monthly target</li>
          <li>Pipeline forecast from bookings</li>
        </ul>
      </div>
      <UpiPayCard amount={price} purpose="homestay_featured" referenceId={property.id} />
    </div>
  );
}
